'use client'

import type { InteractiveButton } from '@/types/interactiveVideo'
import { ArrowUpRight, RotateCcw } from 'lucide-react'
import { FC, useState } from 'react'
import { WIDE_FONT_FAMILY } from './wideFont'

export type WideEndScreenProps = {
  ctaUrl: string | null
  ctaLabel: string | null
  linkButton?: InteractiveButton | null
  onSelectButton?: (buttonId: string) => void | Promise<void>
  onReplay: () => void
}

/** WIDE A の終了状態。最終フレームを残し、操作は浅い下部ストリップへまとめる。 */
const WideEndScreen: FC<WideEndScreenProps> = ({ ctaUrl, ctaLabel, linkButton, onSelectButton, onReplay }) => {
  const [isCtaHovered, setIsCtaHovered] = useState(false)

  return (
    <div
      data-testid="wide-end-screen"
      style={{
        position: 'absolute',
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 30,
        display: 'flex',
        minHeight: '22%',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        gap: '12px',
        padding: '32px clamp(12px, 3vw, 24px) clamp(10px, 2vw, 17px)',
        background: 'linear-gradient(180deg, rgba(8,18,38,0) 0%, rgba(8,18,38,0.32) 42%, rgba(8,18,38,0.82) 100%)',
        color: '#fff',
        fontFamily: WIDE_FONT_FAMILY,
        pointerEvents: 'none',
        animation: 'moovy-wide-end-in 260ms ease-out',
      }}
    >
      <style>{`@keyframes moovy-wide-end-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }`}</style>

      <div
        style={{
          display: 'flex',
          flexShrink: 0,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          gap: '7px',
          pointerEvents: 'auto',
        }}
      >
        <button
          type="button"
          onClick={onReplay}
          style={{
            display: 'inline-flex',
            minHeight: '38px',
            alignItems: 'center',
            gap: '7px',
            padding: '8px 12px',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: '10px',
            background: 'rgba(15,23,42,0.48)',
            color: '#fff',
            fontFamily: 'inherit',
            fontSize: 'clamp(9px, 1.5vw, 12px)',
            fontWeight: 700,
            cursor: 'pointer',
            backdropFilter: 'blur(9px)',
          }}
        >
          <RotateCcw size={14} />
          もう一度見る
        </button>

        {(linkButton || ctaUrl) && (
          <button
            type="button"
            onClick={() => {
              if (linkButton) {
                void onSelectButton?.(linkButton.id)
                return
              }
              if (ctaUrl) window.open(ctaUrl, '_blank', 'noopener,noreferrer')
            }}
            onMouseEnter={() => setIsCtaHovered(true)}
            onMouseLeave={() => setIsCtaHovered(false)}
            onFocus={() => setIsCtaHovered(true)}
            onBlur={() => setIsCtaHovered(false)}
            style={{
              display: 'inline-flex',
              minHeight: '38px',
              alignItems: 'center',
              gap: '7px',
              padding: '8px 13px',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: '10px',
              background: isCtaHovered ? '#fff' : 'rgba(255,255,255,0.93)',
              color: '#172554',
              fontFamily: 'inherit',
              fontSize: 'clamp(9px, 1.5vw, 12px)',
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: '0 10px 25px -15px rgba(0,0,0,0.72)',
              transform: isCtaHovered ? 'translateY(-1px)' : 'translateY(0)',
              transition: 'background 180ms ease, transform 180ms ease',
            }}
          >
            {linkButton?.label || ctaLabel || '詳しく見る'}
            <ArrowUpRight size={14} />
          </button>
        )}
      </div>
    </div>
  )
}

export default WideEndScreen
